/**
 * Public share page — /g/share/:token.
 *
 * No auth, no chrome. Loads /api/share/:token and renders a simple grid.
 * Server returns 410 when the link is revoked or expired — we surface that.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ApiClientError, shareApi, type GalleryItem } from '@/services/api';

type State =
  | { kind: 'loading' }
  | { kind: 'inactive' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; expiresAt: string; items: GalleryItem[] };

export function SharePage() {
  const { token } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: 'not_found' });
      return;
    }
    shareApi
      .get(token)
      .then((res) => {
        if (cancelled) return;
        setState({ kind: 'ready', expiresAt: res.expiresAt, items: res.items });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError) {
          if (err.status === 410) setState({ kind: 'inactive' });
          else if (err.status === 404) setState({ kind: 'not_found' });
          else setState({ kind: 'error', message: err.message });
        } else {
          setState({ kind: 'error', message: 'Failed to load shared gallery' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-secondary-dark text-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wide text-white/40">Shared gallery</p>
          {state.kind === 'ready' && (
            <p className="text-xs text-white/40 mt-1">
              Link expires {new Date(state.expiresAt).toLocaleString()}
            </p>
          )}
        </header>

        {state.kind === 'loading' && <p className="text-white/60">Loading…</p>}
        {state.kind === 'inactive' && (
          <div className="text-center py-16">
            <h1 className="text-3xl font-display font-bold mb-2">This share link is no longer active</h1>
            <p className="text-white/60">It may have expired, been revoked, or hit its use limit.</p>
          </div>
        )}
        {state.kind === 'not_found' && (
          <div className="text-center py-16">
            <h1 className="text-3xl font-display font-bold mb-2">Link not found</h1>
            <p className="text-white/60">Check the URL you were sent.</p>
          </div>
        )}
        {state.kind === 'error' && (
          <div className="text-center py-16">
            <h1 className="text-3xl font-display font-bold mb-2">Could not load gallery</h1>
            <p className="text-white/60">{state.message}</p>
          </div>
        )}
        {state.kind === 'ready' && (
          state.items.length === 0 ? (
            <p className="text-white/60 py-16 text-center">No items in this filter.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {state.items.map((item) => (
                <figure key={item.id} className="bg-white/5 rounded-lg overflow-hidden">
                  {item.mediaKind === 'video' ? (
                    <video src={item.url ?? item.blobUrl} poster={item.posterBlobUrl ?? undefined} controls className="w-full" />
                  ) : (
                    <img src={item.url ?? item.blobUrl} alt={item.caption ?? ''} className="w-full h-auto block" />
                  )}
                  {item.caption && (
                    <figcaption className="px-3 py-2 text-sm text-white/70">{item.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default SharePage;
